import { FunctionComponent, useEffect } from "react";
import { RouteTravelMode } from "@lib/maps/@google/types";
import { useMap } from "@vis.gl/react-google-maps";
import { getMapPadding } from "../calculateMapBounds";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";

interface RoutePolylineProps {
  path?: { lat: number; lng: number }[];
  travelMode?: RouteTravelMode;
  /**
   * Bounds, if present, will adjust map to those bounds.
   */
  bounds?: google.maps.LatLngBounds | null;
}

/**
 * Displays a route polyline, styled for driving or walking.
 */
const RoutePolyline: FunctionComponent<RoutePolylineProps> = ({
  path,
  travelMode = RouteTravelMode.DRIVE,
  bounds,
}: RoutePolylineProps) => {
  const map = useMap();
  const { isMobile } = useWindowSize();

  useEffect(() => {
    if (!map) {
      return;
    }

    let polyline: google.maps.Polyline | null = null;

    if (path?.length ?? 0 > 0) {
      polyline = new google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: VisitWidget.settings.currentClientRouteColor,
        strokeWeight: 4,
        strokeOpacity: travelMode === "WALK" ? 0 : 1,
        map,
        zIndex: 999,
        ...(travelMode === "WALK" && {
          icons: [
            {
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 3,
                fillColor: VisitWidget.settings.currentClientRouteColor,
                fillOpacity: 1,
                strokeColor: VisitWidget.settings.currentClientRouteColor,
              },
              offset: "0",
              repeat: "10px",
            },
          ],
        }),
      });
      if (bounds) {
        map.fitBounds(bounds, getMapPadding(isMobile));
      }

      return () => {
        if (polyline) {
          polyline.setMap(null);
        }
      };
    }
  }, [bounds, isMobile, map, path, travelMode]);

  return <></>;
};

export { RoutePolyline };
